import cx from 'classnames';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { logger } from '../../../core/Log/logger';
import { Typography } from '../../../design-system/components/typography/Typography';
import { WemapLivemap, WemapPinpoint } from '../../../design-system/components/wemap/types/Wemap';
import { boundsFromBoundingBox } from '../../../design-system/components/wemap/utilities/boundsFromBoundingBox';
import { WemapProvider } from '../../../design-system/components/wemap/WemapProvider';
import { WemapReact } from '../../../design-system/components/wemap/WemapReact';
import {
    Destination,
    DestinationTypeRegion,
} from '../../../resources/destination/domain/types/destination';
import { formatPointOfInterestIntoWeMapPinPoint } from '../../../resources/pointOfInterest/domain/operations/formatPlaceForMap';
import { getOnlinePointsOfInterestWithDescription } from '../../../resources/pointOfInterest/domain/operations/getPointsOfInterestWithContent';
import { keepUniquePointsOfInterest } from '../../../resources/pointOfInterest/domain/operations/getUniquePointsOfInterest';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import {
    formatDestinationRegionAsWemapPinpoint,
    formatRegionAsWemapPinpoint,
} from '../../../resources/region/domain/operations/formatRegionAsMapRegion';
import { Region } from '../../../resources/region/domain/types/region';
import { SecondaryRegionListSection } from '../../../shared/components/SecondaryRegionListSection/SecondaryRegionListSection';
import { useDestinationBoundaries } from '../../../shared/hooks/useDestinationBoundaries';
import { useIntersectOnce } from '../../../shared/hooks/useIntersectOnce';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useRegionBoundaries } from '../../../shared/hooks/useRegionBoundaries';
import { IntrinsicElement } from '../../../shared/utilities/types/IntrinsicElement';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './WhereToGoSection.module.css';
import { WhereToGoSectionMapDrawer } from './WhereToGoSectionMapDrawer';
import { WhereToGoSectionMapPanel } from './WhereToGoSectionMapPanel';

export type WhereToGoSectionProps<R, DR, POI> = {
    destination: PickDomain<
        Destination,
        'id' | 'name' | 'preposition' | 'slug',
        {
            geolocation: Destination['geolocation'] | null;
            parentDestination: PickDomain<
                Destination,
                'id' | 'slug' | 'preposition' | 'name'
            > | null;
        }
    >;
    pointsOfInterest: Array<POI>;
    regions: Array<R>;
    destinationsTypeRegion: Array<DR>;
    secondaryRegionTitleElement?: IntrinsicElement;
    regionNameElement?: IntrinsicElement;
};

export function WhereToGoSection<
    R extends PickDomain<
        Region,
        'id' | 'name' | 'slug' | 'isHighlighted' | 'description',
        {
            coverPicture: Region['coverPicture'] | null;
            destination: PickDomain<Region['destination'], 'id' | 'slug' | 'name' | 'preposition'>;
        }
    >,
    DR extends PickDomain<
        DestinationTypeRegion,
        'id' | 'slug' | 'name' | 'coverPicture',
        {
            description: DestinationTypeRegion['description'] | null;
        }
    >,
    POI extends PickDomain<
        PointOfInterest,
        'id' | 'slug' | 'name' | 'isOnline' | 'geolocation',
        {
            destination: PickDomain<Destination, 'slug'>;
            averageRating: PointOfInterest['averageRating'] | null;
            picture: PointOfInterest['picture'] | null;
            geonamesId: PointOfInterest['geonamesId'] | null;
            description: PointOfInterest['description'] | null;
        }
    >,
>({
    pointsOfInterest,
    destination,
    regions,
    destinationsTypeRegion,
    secondaryRegionTitleElement,
    regionNameElement,
}: WhereToGoSectionProps<R, DR, POI>) {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();

    const sectionRef = useRef<HTMLElement>(null);
    const wemapLivemapRef = useRef<WemapLivemap | null>(null);

    const hasUserScrolledCloseToMapSection = useIntersectOnce(sectionRef, {
        root: null,
        rootMargin: '300px',
        threshold: 0,
    });

    const uniquePointsOfInterest = keepUniquePointsOfInterest(
        getOnlinePointsOfInterestWithDescription(pointsOfInterest),
    );

    const pointsOfInterestPinpoints = uniquePointsOfInterest.map((pointOfInterest) =>
        formatPointOfInterestIntoWeMapPinPoint(pointOfInterest),
    );

    const [selectedItem, setSelectedItem] = useState<
        | {
              type: 'destinationRegion';
              item: DR;
          }
        | { type: 'region'; item: R }
        | { type: 'pointOfInterest'; item: POI }
        | null
    >(null);

    const selectedRegion:
        | {
              type: 'destinationRegion';
              item: DR;
          }
        | { type: 'region'; item: R }
        | null =
        selectedItem?.type === 'region' || selectedItem?.type === 'destinationRegion'
            ? (selectedItem as
                  | {
                        type: 'destinationRegion';
                        item: DR;
                    }
                  | { type: 'region'; item: R })
            : null;

    const regionBoundaries = useRegionBoundaries();
    const destinationBoundaries = useDestinationBoundaries();

    const highlightedRegions = regions.filter((region) => {
        return region.isHighlighted;
    });

    const secondaryRegions = regions.filter((region) => {
        return !region.isHighlighted;
    });

    const mapRegion: WemapPinpoint | null = (() => {
        if (selectedItem && selectedItem?.type === 'region') {
            const selectedRegionBoundaries = regionBoundaries.getById(selectedItem.item.id);
            if (selectedRegionBoundaries) {
                return formatRegionAsWemapPinpoint(selectedItem.item, selectedRegionBoundaries);
            }
        }

        if (selectedItem && selectedItem?.type === 'destinationRegion') {
            const selectedDestinationBoundaries = destinationBoundaries.getById(
                selectedItem.item.id,
            );
            if (selectedDestinationBoundaries) {
                return formatDestinationRegionAsWemapPinpoint(
                    selectedItem.item,
                    selectedDestinationBoundaries,
                );
            }
        }

        return null;
    })();

    const mapPinPoints: WemapPinpoint[] = [];
    if (mapRegion) {
        mapPinPoints.push(mapRegion);
    }
    if (pointsOfInterestPinpoints.length > 0) {
        mapPinPoints.push(...pointsOfInterestPinpoints);
    }

    const boundingBox = destination.geolocation?.boundingBox;
    const bounds = boundingBox ? boundsFromBoundingBox(boundingBox) : undefined;

    const openedPinpointId = selectedItem ? selectedItem.item.id : null;

    function handlePinpointClose() {
        setSelectedItem(null);
    }

    function handlePinpointClick({ pinpoint }: { pinpoint: WemapPinpoint }) {
        const pointOfInterest = uniquePointsOfInterest.find(({ id }) => {
            return id === pinpoint.id;
        });

        if (!pointOfInterest) {
            return;
        }

        setSelectedItem({
            type: 'pointOfInterest',
            item: pointOfInterest,
        });
    }

    function handleRegionSelect(region: { type: 'region'; item: R }) {
        setSelectedItem(region);
        void regionBoundaries.fetch(region.item, locale);
    }

    function handleDestinationRegionSelect(destinationRegion: {
        type: 'destinationRegion';
        item: DR;
    }) {
        setSelectedItem(destinationRegion);
        void destinationBoundaries.fetch(destinationRegion.item, locale);
    }

    return (
        <>
            <section className={cx('main-container hidden-xs hidden-sm hidden-md', style.title)}>
                <Typography as="div" scale="secondary-m-bold">
                    {formatMessage({ id: 'page.whereToGo.map.title' })}
                </Typography>
            </section>

            {bounds ? (
                <section
                    ref={sectionRef}
                    className="main-container-cover hidden-xs hidden-sm hidden-md"
                >
                    <div className={style.mapContainer}>
                        {destinationsTypeRegion.length > 0 || highlightedRegions.length > 0 ? (
                            <WhereToGoSectionMapPanel
                                destinationsTypeRegion={destinationsTypeRegion}
                                regions={highlightedRegions}
                                selectedRegion={selectedRegion}
                                onRegionSelect={handleRegionSelect}
                                onDestinationRegionSelect={handleDestinationRegionSelect}
                            />
                        ) : null}
                        <div className={style.mapWrapper}>
                            {selectedItem ? (
                                <WhereToGoSectionMapDrawer
                                    selectedItem={selectedItem}
                                    onClose={() => {
                                        if (selectedItem.type === 'pointOfInterest') {
                                            wemapLivemapRef.current
                                                ?.closePinpoint()
                                                .catch((error) => {
                                                    logger.error('Failed to close pinpoint', {
                                                        error,
                                                    });
                                                })
                                                .then(() => {
                                                    return wemapLivemapRef.current?.easeTo({
                                                        center: selectedItem.item.geolocation
                                                            .middlePoint,
                                                    });
                                                })
                                                .catch((error) => {
                                                    logger.error(
                                                        'Failed to center back to point of interest after drawer closed',
                                                        { pointOnterest: selectedItem.item },
                                                        error,
                                                    );
                                                });
                                        }

                                        if (
                                            selectedItem.type === 'region' ||
                                            selectedItem.type === 'destinationRegion'
                                        ) {
                                            wemapLivemapRef.current
                                                ?.closePinpoint()
                                                .catch((error) => {
                                                    logger.error('Failed to close pinpoint', {
                                                        error,
                                                    });
                                                });
                                        }

                                        setSelectedItem(null);
                                    }}
                                />
                            ) : null}

                            {hasUserScrolledCloseToMapSection ? (
                                <WemapProvider>
                                    <WemapReact
                                        className={style.map}
                                        isDrawerOpened={openedPinpointId !== null}
                                        drawerWidth={WhereToGoSectionMapDrawer.WIDTH}
                                        bounds={bounds}
                                        pinpoints={mapPinPoints}
                                        onPinpointClick={handlePinpointClick}
                                        onPinpointClose={handlePinpointClose}
                                        openedPinpointId={openedPinpointId}
                                        onReady={(livemap) => {
                                            wemapLivemapRef.current = livemap;
                                        }}
                                        trackingInformation={{
                                            placeClicked: {
                                                block: 'where_to_go',
                                                card_number: 0,
                                                click_type: 'button',
                                            },
                                        }}
                                    />
                                </WemapProvider>
                            ) : null}
                        </div>
                    </div>
                </section>
            ) : null}

            {secondaryRegions.length > 0 ? (
                <section className="main-container">
                    <SecondaryRegionListSection
                        destination={
                            destination.parentDestination
                                ? destination.parentDestination
                                : destination
                        }
                        regions={secondaryRegions}
                        titleElement={secondaryRegionTitleElement}
                        regionNameElement={regionNameElement}
                    />
                </section>
            ) : null}
        </>
    );
}
